import type { RecommendationContextAxis } from "@memorang/types";
import type {
	EffectiveMastery,
	FullCell,
} from "../contract/recommendation-context-types";
import { trackKey } from "./cell-model";

const progressionOrder = (axis: RecommendationContextAxis): number[] =>
	[...axis.values]
		.sort(
			(left, right) => left.sortOrder - right.sortOrder || left.id - right.id,
		)
		.map(({ id }) => id);

const unresolvedAt = (
	cells: FullCell[],
	effective: ReadonlyMap<string, EffectiveMastery>,
): FullCell[] =>
	cells.filter((cell) => !effective.get(cell.source.key)?.passed);

const firstUnresolvedValue = ({
	trackCells,
	valueIds,
	progressionAxisId,
	effective,
}: {
	trackCells: FullCell[];
	valueIds: number[];
	progressionAxisId: string;
	effective: ReadonlyMap<string, EffectiveMastery>;
}): FullCell[] => {
	for (const valueId of valueIds) {
		const unresolved = unresolvedAt(
			trackCells.filter(
				(cell) => cell.coordinates[progressionAxisId] === valueId,
			),
			effective,
		);
		if (unresolved.length > 0) {
			return unresolved;
		}
	}
	return [];
};

const collectFirstUnresolvedByTrack = ({
	tracks,
	valueIds,
	progressionAxisId,
	effective,
}: {
	tracks: Iterable<FullCell[]>;
	valueIds: number[];
	progressionAxisId: string;
	effective: ReadonlyMap<string, EffectiveMastery>;
}): FullCell[] =>
	[...tracks].flatMap((trackCells) =>
		firstUnresolvedValue({
			trackCells,
			valueIds,
			progressionAxisId,
			effective,
		}),
	);

export const selectTargetCandidates = ({
	cells,
	axes,
	progressionAxis,
	targetValueId,
	effective,
}: {
	cells: FullCell[];
	axes: RecommendationContextAxis[];
	progressionAxis: RecommendationContextAxis;
	targetValueId: number;
	effective: ReadonlyMap<string, EffectiveMastery>;
}): {
	candidates: FullCell[];
	mode: "PROGRESSION" | "MAINTENANCE";
} => {
	const trackAxes = axes.filter(({ role }) => role === "TRACK");
	const orderedValues = progressionOrder(progressionAxis);
	const targetIndex = orderedValues.indexOf(targetValueId);
	const byTrack = new Map<string, FullCell[]>();
	for (const cell of cells) {
		const key = trackKey(cell, trackAxes);
		byTrack.set(key, [...(byTrack.get(key) ?? []), cell]);
	}

	const throughTarget = collectFirstUnresolvedByTrack({
		tracks: byTrack.values(),
		valueIds: orderedValues.slice(0, targetIndex + 1),
		progressionAxisId: progressionAxis.id,
		effective,
	});
	if (throughTarget.length > 0) {
		return { candidates: throughTarget, mode: "PROGRESSION" };
	}

	const aboveTarget = collectFirstUnresolvedByTrack({
		tracks: byTrack.values(),
		valueIds: orderedValues.slice(targetIndex + 1),
		progressionAxisId: progressionAxis.id,
		effective,
	});
	return aboveTarget.length > 0
		? { candidates: aboveTarget, mode: "PROGRESSION" }
		: { candidates: cells, mode: "MAINTENANCE" };
};
